import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export default function useModalDialog({ isOpen, dialogRef, returnFocusRef, onClose }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return undefined;

    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const layer = dialog.closest("[data-modal-layer]");
    const returnFocus = returnFocusRef?.current;
    const parent = layer?.parentElement;
    const siblings = parent
      ? Array.from(parent.children).filter((child) => child !== layer)
      : [];
    const previousSiblingState = siblings.map((sibling) => ({
      sibling,
      inert: sibling.inert,
      inertAttribute: sibling.hasAttribute("inert"),
      ariaHidden: sibling.getAttribute("aria-hidden"),
    }));
    const previousOverflow = document.body.style.overflow;

    siblings.forEach((sibling) => {
      sibling.inert = true;
      sibling.setAttribute("inert", "");
      sibling.setAttribute("aria-hidden", "true");
    });
    document.body.style.overflow = "hidden";

    const focusable = () => Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
    const firstTarget = focusable()[0] || dialog;
    const frame = window.requestAnimationFrame(() => firstTarget.focus());

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== "Tab") return;

      const targets = focusable();
      if (targets.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = targets[0];
      const last = targets[targets.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousSiblingState.forEach(({ sibling, inert, inertAttribute, ariaHidden }) => {
        sibling.inert = inert;
        if (!inertAttribute) sibling.removeAttribute("inert");
        if (ariaHidden === null) sibling.removeAttribute("aria-hidden");
        else sibling.setAttribute("aria-hidden", ariaHidden);
      });
      window.requestAnimationFrame(() => returnFocus?.focus());
    };
  }, [dialogRef, isOpen, returnFocusRef]);
}
